import { useAnalytics } from '@backstage/core-plugin-api';

import {
  extractLastNameComponent,
  invariant,
} from '@spotify/confidence-core-react';
import {
  CollaborationV1AnchorInput,
  CollaborationV1CommentReplyAction,
  CommentFragment,
  CommentFragmentDoc,
  CommentRepliesDocument,
  CommentRepliesQuery,
  CommentsDocument,
  CommentsQuery,
  IdentityRefFragment,
  getError,
  getTypeOrNull,
  isError,
  useCreateCommentMutation,
  useCreateCommentReplyMutation,
  useDeleteCommentMutation,
} from '@spotify/confidence-graphql';
import { useCurrentUser } from '@spotify/plugin-confidence-iam-react';

import { type Anchor } from '../../types';
import { reactionSummaryNameFromResource } from '../../utils';
import { updateCommentRepliesQuery } from '../useCommentReplies';
import { useCommentState } from '../useCommentState';
import { updateCommentsQuery } from '../useComments';

const generateRandomCommentId = (workflowInstance: string) => {
  const lastComponent = extractLastNameComponent(workflowInstance);
  return `${lastComponent}-${Math.random().toString(36).substring(7)}`;
};

const toAnchor = (anchor: Anchor): CollaborationV1AnchorInput => ({
  zone: { zone: anchor.zone },
  document: null,
});

export const useCommentApi = (resource: string) => {
  const { currentUser } = useCurrentUser();
  const analytics = useAnalytics();
  const listeners = useCommentState(state => state.listeners);

  // The Identity we use for optimistic updates
  const author = getTypeOrNull(currentUser?.identity) || undefined;

  const [addCommentMutation] = useCreateCommentMutation({});
  const [addReplyMutation] = useCreateCommentReplyMutation();
  const [deleteCommentMutation] = useDeleteCommentMutation();

  const addComment = async (
    content: string,
    anchor?: Anchor,
    assignee?: IdentityRefFragment,
  ) => {
    invariant(author, 'must have an author to add a comment');

    const commentId = generateRandomCommentId(resource);
    const response = await addCommentMutation({
      variables: {
        commentId,
        comment: {
          resource,
          content,
          anchor: anchor ? toAnchor(anchor) : null,
          assignee: assignee?.name,
        },
      },
      optimisticResponse: {
        createComment: {
          __typename: 'CollaborationV1Comment',
          name: `comments/${commentId}`,
          content,
          resource: resource,
          resolved: false,
          anchor: anchor ? toAnchor(anchor) : null,
          assignee: assignee ?? null,
          author,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(`comments/${commentId}`),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      update: (cache, { data: result }) => {
        const comment = getTypeOrNull(result?.createComment);

        if (!comment) return;

        cache.updateQuery<CommentsQuery>(
          {
            query: CommentsDocument,
            variables: {
              resource,
              filter: null,
            },
          },
          data => updateCommentsQuery(data, comment),
        );
      },
    });

    if (response.errors) {
      throw response.errors[0];
    }

    // Check for errors in the result (will be caught by caller's try-catch)
    if (response.data && isError(response.data.createComment)) {
      throw getError(response.data.createComment);
    }

    const commentName = getTypeOrNull(response?.data?.createComment)?.name;

    analytics.captureEvent('add-comment', resource, {
      value: content.length,
      attributes: {
        zone: anchor?.zone ?? 'unknown',
        assign: Boolean(assignee),
        commentName: commentName ?? '',
      },
    });

    // Notify listeners that comment was created
    if (commentName && anchor) {
      listeners.forEach(listener => {
        listener({
          anchor,
          threadId: commentName,
          reason: 'created',
          data: response.data,
        });
      });
    }

    return commentName;
  };

  const deleteComment = async (commentName: string) => {
    analytics.captureEvent('delete-comment', resource, {
      attributes: { commentName },
    });

    await deleteCommentMutation({
      variables: {
        commentName,
      },
      optimisticResponse: {
        deleteComment: {
          __typename: 'CollaborationV1Comment',
        },
      },
      update: cache => {
        cache.modify({
          fields: {
            comments(existing = { comments: [] }, { storeFieldName }) {
              if (storeFieldName.includes(resource)) {
                return {
                  ...existing,
                  comments: existing.comments.filter(
                    (c: { name: string }) => c.name !== commentName,
                  ),
                };
              }

              return existing;
            },
          },
        });
      },
    });
  };

  const addReply = async (
    commentName: string,
    content: string,
    assignee?: IdentityRefFragment,
  ): Promise<string | undefined> => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    const response = await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          content,
          assignee: assignee?.name,
          action: assignee
            ? CollaborationV1CommentReplyAction.Assign
            : undefined,
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content,
          author,
          assignee: assignee ?? null,
          action: assignee ? CollaborationV1CommentReplyAction.Assign : null,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        if (!commentReply) return;

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return null;

            return {
              ...existing,
              assignee: assignee
                ? {
                    // When an assignee is set, Apollo cache requires all
                    // fields to be present, even if they are null.
                    user: null,
                    apiClient: null,
                    group: null,
                    everyone: null,
                    ...assignee,
                  }
                : null,
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply),
        );
      },
    });

    if (response.errors) {
      throw response.errors[0];
    }

    // Check for errors in the result (will be caught by caller's try-catch)
    if (response.data && isError(response.data.createCommentReply)) {
      throw getError(response.data.createCommentReply);
    }

    analytics.captureEvent('add-reply', resource, {
      value: content.length,
      attributes: {
        assign: Boolean(assignee),
        commentReplyId,
        inReplyTo: commentName,
      },
    });

    return commentReplyId;
  };

  const resolveComment = async (commentName: string) => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    analytics.captureEvent('resolve-thread', resource, {
      attributes: { commentId: commentName },
    });

    const resolveResult = await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          action: CollaborationV1CommentReplyAction.Resolve,
          content: '',
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content: '',
          author,
          assignee: null,
          action: CollaborationV1CommentReplyAction.Resolve,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        if (!commentReply) return;

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return null;

            return {
              ...existing,
              resolved: true,
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply),
        );
      },
    });

    // Check for errors in the result (will be caught by caller's try-catch)
    if (resolveResult.data && isError(resolveResult.data.createCommentReply)) {
      throw getError(resolveResult.data.createCommentReply);
    }
  };

  const reopenComment = async (commentName: string) => {
    invariant(author, 'must have an author to add a comment');
    const commentReplyId = generateRandomCommentId(resource);

    analytics.captureEvent('reopen-thread', resource, {
      attributes: { commentId: commentName },
    });

    const reopenResult = await addReplyMutation({
      variables: {
        commentName,
        commentReplyId,
        reply: {
          action: CollaborationV1CommentReplyAction.Reopen,
          content: '',
        },
      },
      optimisticResponse: {
        createCommentReply: {
          __typename: 'CollaborationV1CommentReply',
          name: `${commentName}/replies/${commentReplyId}`,
          content: '',
          author,
          assignee: null,
          action: CollaborationV1CommentReplyAction.Reopen,
          createTime: new Date().toISOString(),
          updateTime: new Date().toISOString(),
          queryReactionSummary: {
            __typename: 'CollaborationV1QueryReactionSummaryResponse',
            summary: {
              name: reactionSummaryNameFromResource(
                `${commentName}/replies/${commentReplyId}`,
              ),
              commentCount: 0,
              emojiReactions: [],
            },
          },
        },
      },
      update: (cache, { data: result }) => {
        const commentReply = getTypeOrNull(result?.createCommentReply);

        if (!commentReply) return;

        cache.updateFragment<CommentFragment>(
          {
            fragment: CommentFragmentDoc,
            fragmentName: 'Comment',
            id: cache.identify({
              __typename: 'CollaborationV1Comment',
              name: commentName,
            }),
          },
          existing => {
            if (!existing) return null;

            return {
              ...existing,
              resolved: false,
            };
          },
        );

        cache.updateQuery<CommentRepliesQuery>(
          {
            query: CommentRepliesDocument,
            variables: {
              parent: commentName,
              pageSize: 5,
            },
          },
          data => updateCommentRepliesQuery(data, commentReply),
        );
      },
    });

    // Check for errors in the result (will be caught by caller's try-catch)
    if (reopenResult.data && isError(reopenResult.data.createCommentReply)) {
      throw getError(reopenResult.data.createCommentReply);
    }
  };

  return {
    addComment,
    deleteComment,
    addReply,
    resolveComment,
    reopenComment,
  };
};
